/*
  Locale URL prefix (DEV-3681). Non-English pages live under a prefix at the
  start of the path: /de/sbc. English is unprefixed. The URL is the only
  thing that decides a page's language: no stored preference ever redirects
  or re-renders a URL, so crawlers and shared links see one page per URL.
  The table is explicit rather than derived from the locale tag so a future
  pt-BR can be "/pt-br". Mirrored in aws-utilities/generate_sitemap; the two
  must agree.
*/
import { UI_LOCALES, uiLocalePrimarySubtag } from "./uiLocale";
export const LOCALE_PREFIXES = {
    "en-US": "",
    "da-DK": "/da",
    "de-DE": "/de",
    "es-ES": "/es",
    "fr-FR": "/fr",
    "pt-PT": "/pt",
    "it-IT": "/it",
    "tr-TR": "/tr",
};
export const localePrefix = (locale) => LOCALE_PREFIXES[locale];
/**
 * The locale a pathname carries and the path after the prefix. Only an exact
 * first-segment match counts: "/design-system" is not "/de". Unprefixed paths
 * are en-US with the path untouched. The path is expected without any
 * edition route base in front of it.
 */
export const splitLocalePrefix = (pathname) => {
    const segment = `/${pathname.split("/")[1] ?? ""}`;
    for (const locale of UI_LOCALES) {
        const prefix = LOCALE_PREFIXES[locale];
        if (prefix && prefix === segment) {
            return { locale, rest: pathname.slice(prefix.length) || "/" };
        }
    }
    return { locale: "en-US", rest: pathname };
};
/**
 * `path` (no prefix, no edition base) under `locale`. The root becomes the
 * bare prefix, "/de" not "/de/": that is what react-router emits for a home
 * link under a basename, and the canonical form the sitemap advertises.
 */
export const withLocalePrefix = (path, locale) => {
    const prefix = LOCALE_PREFIXES[locale];
    const slashed = path.startsWith("/") ? path : `/${path}`;
    return prefix && slashed === "/" ? prefix : `${prefix}${slashed}`;
};
/**
 * hreflang set for a canonical URL (DEV-3684): one entry per UI locale plus
 * `x-default`, the prefix inserted after `editionBase`. A prefix already in
 * the URL is replaced, so every sibling page yields the same set. `en` and
 * `x-default` both point at the unprefixed URL. Codes are primary subtags,
 * equal to the prefix segment. Trailing slash dropped except at the root.
 */
export const localeAlternates = (canonical, editionBase = "") => {
    const match = /^(https?:\/\/[^/]*)(\/.*)?$/.exec(canonical);
    const origin = match ? match[1] : "";
    const path = match ? (match[2] ?? "/") : canonical;
    const underEdition = editionBase && path.startsWith(editionBase)
        ? path.slice(editionBase.length) || "/"
        : path;
    const { rest } = splitLocalePrefix(underEdition);
    const href = (locale) => {
        const full = `${editionBase}${withLocalePrefix(rest, locale)}`;
        return `${origin}${full.length > 1 && full.endsWith("/") ? full.slice(0, -1) : full}`;
    };
    return [
        ...UI_LOCALES.map((locale) => ({
            hreflang: uiLocalePrimarySubtag(locale),
            href: href(locale),
        })),
        { hreflang: "x-default", href: href("en-US") },
    ];
};
//# sourceMappingURL=localePath.js.map